import {
  AudioVideoComponent,
  ButtonComponent,
  CarouselComponent,
  ColumnComponent,
  DividerComponent,
  EmbedComponent,
  FileComponent,
  FileUploaderComponent,
  FillBlankComponent,
  FlipCardComponent,
  FreeTextComponent,
  FullwidthImageComponent,
  FullwidthVideoComponent,
  GalleryComponent,
  HotspotComponent,
  IconTextComponent,
  ImageComponent,
  ListComponent,
  ScreenCaptureComponent,
  SelectionComponent,
  SequencerComponent,
  SliderComponent,
  SorterComponent,
  TableComponent,
  TextComponent,
  TimelineComponent
} from '../models/LessonComponent';
import {
  ButtonComponentSettings,
  ColumnComponentSettings,
  DividerComponentSettings,
  FileComponentSettings,
  FileUploaderComponentSettings,
  FillBlankComponentSettings,
  FreeTextComponentSettings,
  FullwidthImageComponentSettings,
  GalleryComponentSettings,
  HotspotComponentSettings,
  IconTextComponentSettings,
  ImageComponentSettings,
  LessonComponentSettings,
  ListComponentSettings,
  ScreenCaptureComponentSettings,
  SelectionComponentSettings,
  SequencerComponentSettings,
  SliderComponentSettings,
  SorterComponentSettings,
  TableComponentSettings,
  TimelineComponentSettings
} from '../models/LessonComponentSettings';
import {
  CarouselItem,
  Cell,
  ColumnAsset,
  FlipCardItem,
  HotspotItem,
  SorterCategory,
  TableRowItem,
  TimelineAsset
} from '../models/LessonComponentContent';

import Constants from '../constants';
import { LessonComponentStyle } from '../models/LessonComponentStyle';
import { LessonComponentType } from '../models/LessonComponentType';
import LessonService from './LessonService';
import { LessonType } from '../../dashboard/models/LessonVm';
import { nanoid } from 'nanoid';

class NewComponentService {
  private lastComponentkey = -1; //* This is used to assign the componentkey for a new component.
  //* Negative values are used to differentiate new components from exsiting ones.
  get NewComponentkey() {
    return this.lastComponentkey--;
  }

  /* 'ql-align-center' class added to the caption text to set it 'center aligned'
    as default. Quill editor updates this class according to the alignment. If it is right aligned
    it adds 'ql-align-right' and if it is left aligned it doesn't add any class. */
  private captionText = '';
  private defaultPoints = 1;
  public centeredText = "<p class='ql-align-center'><br/></p>";

  /** Duration, in seconds, where the end of the video counts as played. */
  private VIDEO_ENDED_GRACE_s = 5;
  /** Default column count for Table component */
  private DEFAULT_COLUMN_COUNT = 3;

  /** Resetting these styles in LessonComponentSettingsDialog when stretchBackground option is on */
  public resetStyles: LessonComponentStyle = {
    border: {
      width: 0,
      style: 'none',
      color: null
    },
    corner: {
      topLeft: 0,
      topRight: 0,
      bottomLeft: 0,
      bottomRight: 0
    },
    shadow: { x: 0, y: 0, b: 0, color: null }
  };

  public get DefaultStyle() {
    const defaultStyles: LessonComponentStyle = {
      backgroundColor: {
        r: 255,
        g: 255,
        b: 255,
        a: 1
      },
      margin: {
        top: 15,
        right: 0,
        bottom: 15,
        left: 0
      },
      padding: {
        top: 30,
        right: 30,
        bottom: 30,
        left: 30
      },
      stretchBackground: false,
      ...this.resetStyles
    };
    return defaultStyles;
  }

  public get DefaultFullwidthStyle() {
    return {
      margin: {
        top: 15,
        right: 0,
        bottom: 15,
        left: 0
      }
    } as LessonComponentStyle;
  }

  public getDefaultSelectionSettings(
    type?: LessonComponentType
  ): SelectionComponentSettings {
    const defaultSettings: SelectionComponentSettings = {
      points: this.defaultPoints,
      correctFeedback: 'Correct',
      incorrectFeedback: 'Incorrect',
      randomizeOptions: false,
      imagePosition: 'bottom',
      selectionSettings: { showBorder: false, displayMode: 'none' },
      assetkey: ''
    };
    return defaultSettings;
  }

  public get DefaultButtonSettings() {
    return {
      buttonLabel: 'Button',
      link: ''
    } as ButtonComponentSettings;
  }

  public get DefaultDividerSettings() {
    return {
      dividerStyles: {
        corner: { topLeft: 10, topRight: 10, bottomLeft: 10, bottomRight: 10 },
        shadow: { x: 5, y: 0, b: 5, color: null },
        border: {
          width: 1,
          style: 'solid',
          color: { r: 0, g: 0, b: 0, a: 1 }
        }
      } as LessonComponentStyle
    } as DividerComponentSettings;
  }

  public get DefaultFreeTextSettings(): FreeTextComponentSettings {
    return {
      points: this.defaultPoints,
      instructionText: 'Type your answer here'
    };
  }

  public get DefaultImageSettings(): ImageComponentSettings {
    return { assetkey: '', zoom: false };
  }

  public get DefaultFileSettings(): FileComponentSettings {
    return { assetkey: '' };
  }

  public get DefaultFullwidthImageSettings(): FullwidthImageComponentSettings {
    return { assetkey: '', imageHeight: 50, imagePosition: 'center' };
  }

  public get DefaultSorterSettings(): SorterComponentSettings {
    return {
      points: this.defaultPoints,
      correctFeedback: 'Correct',
      incorrectFeedback: 'Incorrect',
      sorterDisplayMode: 'image_with_label'
    };
  }

  public get DefaultSequencerSettings(): SequencerComponentSettings {
    return {
      points: this.defaultPoints,
      correctFeedback: `You've answered the question correctly.`,
      incorrectFeedback: 'Not quite. Try again.'
    };
  }

  public get DefaultSliderSettings(): SliderComponentSettings {
    return {
      points: this.defaultPoints,
      correctFeedback: `You've answered the question correctly.`,
      incorrectFeedback: 'Not quite. Try again.'
    };
  }

  public get DefaultFileUploaderSettings(): FileUploaderComponentSettings {
    return {
      points: this.defaultPoints,
      fileType: ['audio', 'document', 'image', 'video']
    };
  }

  public get DefaultHotspotSettings(): HotspotComponentSettings {
    return { assetkey: '' };
  }

  private get DefaultFillblankSettings(): FillBlankComponentSettings {
    return {
      points: this.defaultPoints,
      correctFeedback: 'Correct',
      incorrectFeedback: 'Incorrect'
    };
  }

  private get DefaultScreenCaptureSettings(): ScreenCaptureComponentSettings {
    return {
      assetkey: '',
      points: this.defaultPoints
    };
  }

  public get DefaultListSettings() {
    return {
      assetkey: '',
      listSettings: {
        backgroundColor: null,
        foregroundColor: {
          r: 255,
          g: 255,
          b: 255,
          a: 1
        },
        listType: 'numbered_circle',
        startNumber: 1
      }
    } as ListComponentSettings;
  }

  public get DefaultColumnSettings(): ColumnComponentSettings {
    return { textPosition: 'center' };
  }

  public get DefaultTableSettings(): TableComponentSettings {
    return {
      tableSettings: {
        borderColor: {
          r: 228,
          g: 228,
          b: 228,
          a: 1
        },
        headerBgColor: {
          r: 255,
          g: 255,
          b: 255,
          a: 1
        },
        dataBgColor: {
          r: 255,
          g: 255,
          b: 255,
          a: 1
        }
      }
    };
  }

  public get DefaultIconTextSettings(): IconTextComponentSettings {
    return {
      assetkey: '',
      iconSettings: { position: 'center', shape: 'circle', size: 100 }
    };
  }

  public get DefaultGallerySettings(): GalleryComponentSettings {
    return {
      gallerySettings: {
        shape: 'square',
        columns: 3,
        padding: 10,
        aspectRatio: '1/1'
      }
    };
  }

  public get DefaultTimelineSettings(): TimelineComponentSettings {
    return {
      primaryColor: null
    };
  }

  public defaultSettings: {
    [type in LessonComponentType]: LessonComponentSettings;
  } = {
    heading: {},
    subheading: {},
    paragraph: {},
    single_selection: this.getDefaultSelectionSettings('single_selection'),
    multiple_selection: this.getDefaultSelectionSettings('multiple_selection'),
    free_text: this.DefaultFreeTextSettings,
    image: this.DefaultImageSettings,
    video: {},
    audio: {},
    file: {},
    button: this.DefaultButtonSettings,
    embed: {},
    divider: this.DefaultDividerSettings,
    sorter: this.DefaultSorterSettings,
    video_uploader: {},
    full_width_image: this.DefaultFullwidthImageSettings,
    full_width_video: {},
    carousel: {},
    hotspot: this.DefaultHotspotSettings,
    fill_blank: this.DefaultFillblankSettings,
    fill_menu: this.DefaultFillblankSettings,
    sequencer: this.DefaultSequencerSettings,
    slider: this.DefaultSliderSettings,
    file_uploader: this.DefaultFileUploaderSettings,
    screen_capture: this.DefaultScreenCaptureSettings,
    screen_capture_comparison: this.DefaultScreenCaptureSettings,
    single_selection_card: this.getDefaultSelectionSettings(),
    multiple_selection_card: this.getDefaultSelectionSettings(),
    list: this.DefaultListSettings,
    flip_card: {},
    column: this.DefaultColumnSettings,
    table: this.DefaultTableSettings,
    icon_text: this.DefaultIconTextSettings,
    gallery: this.DefaultGallerySettings,
    timeline: this.DefaultTimelineSettings
  };

  public generateLessonComponent(
    lessonType: LessonType,
    type: LessonComponentType
  ) {
    let newComponent;
    switch (type) {
      case 'heading':
      case 'subheading':
      case 'paragraph':
        newComponent = this.generateTextComponent(type);
        break;
      case 'list':
        newComponent = this.generateListComponent(type);
      case 'single_selection':
      case 'multiple_selection':
      case 'single_selection_card':
      case 'multiple_selection_card':
        newComponent = this.generateSelectionComponent(type);
        break;
      case 'free_text':
        newComponent = this.generateFreeTextComponent(lessonType, type);
        break;
      case 'image':
        newComponent = this.generateImageComponent(type);
        break;
      case 'video':
      case 'audio':
        newComponent = this.generateAudioVideoComponent(type);
        break;
      case 'file':
        newComponent = this.generateFileComponent(type);
        break;
      case 'button':
        newComponent = this.generateButtonComponent(type);
        break;
      case 'embed':
        newComponent = this.generateEmbedComponent(type);
        break;
      case 'divider':
        newComponent = this.generateDividerComponent(type);
        break;
      case 'sorter':
        newComponent = this.generateSorterComponent(type);
        break;
      case 'full_width_image':
        newComponent = this.generateFullwidthImageComponent(type);
        break;
      case 'full_width_video':
        newComponent = this.generateFullwidthVideoComponent(type);
        break;
      case 'carousel':
        newComponent = this.generateCarouselComponent(type);
        break;
      case 'hotspot':
        newComponent = this.generateHotspotComponent(type);
        break;
      case 'sequencer':
        newComponent = this.generateSequencerComponent(type);
        break;
      case 'slider':
        newComponent = this.generateSliderComponent(type);
        break;
      case 'fill_blank':
      case 'fill_menu':
        newComponent = this.generateFillblankComponent(type);
        break;
      case 'file_uploader':
        newComponent = this.generateFileUploaderComponent(lessonType, type);
        break;
      case 'screen_capture':
      case 'screen_capture_comparison':
        newComponent = this.generateScreenCaptureComponent(lessonType, type);
        break;
      case 'flip_card':
        newComponent = this.generateFlipCardComponent(type);
        break;
      case 'column':
        newComponent = this.generateColumnComponent(type);
        break;
      case 'table':
        newComponent = this.generateTableComponent(type);
        break;
      case 'icon_text':
        newComponent = this.generateIconTextComponent(type);
        break;
      case 'gallery':
        newComponent = this.generateGalleryComponent(type);
        break;
      case 'timeline':
        newComponent = this.generateTimelineComponent(type);
        break;
      default:
        newComponent = null;
    }

    if (newComponent) {
      newComponent.componentkey = this.NewComponentkey;
    }

    return newComponent;
  }

  private getNonAutoMarkingDefaultPoints(lessonType: LessonType) {
    return LessonService.isAutoMarkingLesson(lessonType)
      ? 0 //* in 'Basic' and 'Quiz' lesson types
      : this.defaultPoints;
  }

  private generateTextComponent(type: LessonComponentType) {
    const text = '';
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        text
      },
      required: false
    } as TextComponent;
  }

  private generateListComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        options: []
      },
      required: false
    } as ListComponent;
  }

  private generateSelectionComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        question: '',
        options: []
      },
      required: false
    } as SelectionComponent;
  }

  private generateButtonComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        caption: this.captionText
      },
      required: false
    } as ButtonComponent;
  }

  private generateEmbedComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        embedCode: ''
      },
      required: false
    } as EmbedComponent;
  }

  private generateDividerComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {},
      required: false
    } as DividerComponent;
  }

  private generateFreeTextComponent(
    lessonType: LessonType,
    type: LessonComponentType
  ) {
    return {
      type,
      style: this.DefaultStyle,
      settings: {
        ...this.defaultSettings[type],
        points: this.getNonAutoMarkingDefaultPoints(lessonType)
      },
      content: {},
      required: false
    } as FreeTextComponent;
  }

  private generateImageComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: { caption: this.captionText },
      required: false
    } as ImageComponent;
  }

  private generateFullwidthImageComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultFullwidthStyle,
      settings: this.defaultSettings[type],
      content: {},
      required: false
    } as FullwidthImageComponent;
  }

  private generateFullwidthVideoComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultFullwidthStyle,
      settings: this.defaultSettings[type],
      content: {},
      required: false
    } as FullwidthVideoComponent;
  }

  private generateAudioVideoComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: { caption: this.captionText },
      required: false
    } as AudioVideoComponent;
  }

  private generateFileComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {},
      required: false
    } as FileComponent;
  }

  private generateSorterComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        categories: [this.createSorterCategory(), this.createSorterCategory()] //* Two categories at least.
      },
      required: false
    } as SorterComponent;
  }

  public createSorterCategory(): SorterCategory {
    return {
      label: '',
      id: nanoid(),
      items: []
    };
  }

  private generateCarouselComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        carousel: [this.createCarousel()]
      },
      required: false
    } as CarouselComponent;
  }

  public createCarousel(): CarouselItem {
    return {
      id: nanoid(),
      heading: this.centeredText,
      body: this.centeredText,
      image: ''
    };
  }

  private generateHotspotComponent(type: LessonComponentType) {
    const res = {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        hotspot: []
      },
      required: false
    } as HotspotComponent;

    return res;
  }

  public createHotspot(): HotspotItem {
    return {
      id: nanoid(),
      position: { x: 0, y: 0 },
      heading: this.centeredText,
      body: this.centeredText
    };
  }

  private generateFillblankComponent(type: LessonComponentType) {
    const component = {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        text: '',
        blank: []
      },
      required: false
    } as FillBlankComponent;
    if (type === 'fill_menu') {
      component.content.correctResponsePattern = JSON.stringify({});
    }
    return component;
  }

  private generateSequencerComponent(
    type: LessonComponentType
  ): SequencerComponent {
    const component: SequencerComponent = {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        sequencer: {
          topLabel: '',
          bottomLabel: '',
          items: []
        }
      },
      position: null,
      published: null,
      parentkey: null,
      required: false
    };
    return component;
  }

  private generateSliderComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        slider: []
      },
      required: false
    } as SliderComponent;
  }

  private generateFileUploaderComponent(
    lessonType: LessonType,
    type: LessonComponentType
  ) {
    return {
      type,
      style: this.DefaultStyle,
      settings: {
        ...this.defaultSettings[type],
        points: this.getNonAutoMarkingDefaultPoints(lessonType)
      },
      content: {},
      required: false
    } as FileUploaderComponent;
  }

  private generateScreenCaptureComponent(
    lessonType: LessonType,
    type: LessonComponentType
  ) {
    return {
      type,
      style: this.DefaultStyle,
      settings: {
        ...this.defaultSettings[type],
        points: this.getNonAutoMarkingDefaultPoints(lessonType)
      },
      content: {
        analysisTool: { id: '', captures: [] }
      },
      required: false
    } as ScreenCaptureComponent;
  }

  private generateFlipCardComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        caption: this.centeredText,
        flipCard: [this.createFlipCard()]
      },
      required: false
    } as FlipCardComponent;
  }

  public createFlipCard(): FlipCardItem {
    return {
      id: nanoid(),
      frontLabel: '',
      frontImage: '',
      backLabel: '',
      backImage: ''
    };
  }

  private generateColumnComponent(type: LessonComponentType) {
    return {
      type,
      style: {
        ...this.DefaultStyle,
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      },
      settings: this.defaultSettings[type],
      content: {
        column: this.createColumns(Constants.ColumnComponentDefaultItems)
      },
      required: false
    } as ColumnComponent;
  }

  public createColumns(numColumns: number): ColumnAsset[] {
    const columns: ColumnAsset[] = [];
    for (let i = 0; i < numColumns; i++) {
      columns.push({
        text: '',
        assetkey: '',
        backgroundColor: null
      });
    }
    return columns;
  }

  public generateTableCells(
    columnCount = this.DEFAULT_COLUMN_COUNT,
    header?: boolean
  ) {
    let cells: Cell[] = [];
    for (let index = 0; index < columnCount; index++) {
      cells.push(
        header
          ? {
              id: nanoid(), // For header it is necessary to have unique key for <Draggable> item
              text: ''
            }
          : { text: '' }
      );
    }
    return cells;
  }

  private createTableRowElements() {
    const defaultRows: TableRowItem[] = [];
    for (let index = 0; index < this.DEFAULT_COLUMN_COUNT - 1; index++) {
      defaultRows.push({ id: nanoid(), cells: this.generateTableCells() });
    }
    return defaultRows;
  }

  public generateTableComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        table: {
          header: {
            id: nanoid(),
            cells: this.generateTableCells(this.DEFAULT_COLUMN_COUNT, true)
          },
          rows: this.createTableRowElements()
        }
      },
      required: false
    } as TableComponent;
  }

  private generateIconTextComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {},
      required: false
    } as IconTextComponent;
  }

  private generateGalleryComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        column: this.createColumns(Constants.GalleryComponentDefaultItems)
      },
      required: false
    } as GalleryComponent;
  }

  public createTimelineItem(): TimelineAsset {
    return {
      id: nanoid(),
      dateAndTime: '',
      heading: '',
      paragraph: '',
      itemColor: '',
      itemIcon: '',
      itemBorder: ''
    };
  }

  private generateTimelineComponent(type: LessonComponentType) {
    return {
      type,
      style: this.DefaultStyle,
      settings: this.defaultSettings[type],
      content: {
        timeline: [this.createTimelineItem()]
      }
    } as TimelineComponent;
  }
}

export default new NewComponentService();
